/**
 * Cookie consent helpers.
 *
 * South Africa's POPIA requires that non-essential cookies (including analytics
 * and advertising cookies) are only set after the visitor gives consent. GA4 is
 * therefore never initialised eagerly — AnalyticsProvider waits for consent and
 * only then mounts the GoogleAnalytics component, so no gtag script runs and no
 * _ga cookie is written for a visitor who declined.
 *
 * The consent cookie is written by react-cookie-consent with
 * cookieName="zamzi-connect-cookie-consent" (see components/layout/CookieConsentBanner).
 */

export const CONSENT_COOKIE = 'zamzi-connect-cookie-consent';

export type ConsentState = 'unknown' | 'granted' | 'denied';

export function readConsent(): ConsentState {
  if (typeof document === 'undefined') return 'unknown';
  const raw = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CONSENT_COOKIE}=`));
  if (!raw) return 'unknown';
  const value = decodeURIComponent(raw.slice(CONSENT_COOKIE.length + 1)).split(';')[0].trim();
  if (value === 'true') return 'granted';
  if (value === 'false') return 'denied';
  return 'unknown';
}

export function hasAnalyticsConsent(): boolean {
  return readConsent() === 'granted';
}

/** Fire a callback whenever consent is granted. Returns an unsubscribe fn. */
export function onConsentGranted(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  if (readConsent() === 'granted') {
    callback();
    return () => {};
  }
  const handler = (event: Event) => {
    // react-cookie-consent dispatches detail.consent as the string 'true'/'false'.
    const detail = (event as CustomEvent<{ consent?: string }>).detail;
    if (detail?.consent === 'granted' || detail?.consent === 'true') {
      window.removeEventListener('cookieConsent', handler);
      callback();
    }
  };
  window.addEventListener('cookieConsent', handler);
  // Also poll briefly — react-cookie-consent only dispatches the event for
  // changes made in this session, not for a consent granted in a previous one.
  let attempts = 0;
  const poll = setInterval(() => {
    if (readConsent() === 'granted') {
      clearInterval(poll);
      window.removeEventListener('cookieConsent', handler);
      callback();
      return;
    }
    if (++attempts > 30) clearInterval(poll);
  }, 500);
  return () => {
    clearInterval(poll);
    window.removeEventListener('cookieConsent', handler);
  };
}
